import React, { useState, useRef, useEffect } from 'react';
import { Pie, Bar } from 'react-chartjs-2';
import config from 'config/config';
import { useErrorHandler } from 'react-error-boundary';
import {
    handleFetchError,
    formatAttributesWithCommas,
    determineChartSteps,
} from 'utilities/utils';

// Mui:
import Button from '@mui/material/Button';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Styles:
import muiTheme from '../../theme';

const labels = [
    'White NH',
    'Black NH',
    'Asian NH',
    'Hispanic',
    'Other NH',
    'Two or more NH',
];

const CommunityProfile = ({
    currentTopic,
    currentTab,
    mapDemographicContent,
    chartDemographicContent,
    setChartInfoModalOpen,
}) => {
    // Refs:
    const pieRef = useRef();
    const barRef = useRef();

    // State:
    const [raceData, setRaceData] = useState({});
    const [ageData, setAgeData] = useState({});
    const [acsLink, setAcsLink] = useState('');
    const [state, setState] = useState({
        // Jurisdiction codes:
        MDCode: '',
        GEOID: '',
        Geotype: '',
        // General population variables:
        EstTotalPopFormat: '',
        // Age variables:
        EstFemaleTotalPopFormat: '',
        CalcPctFemaleTotalPop: '',
        EstMaleTotalPopFormat: '',
        CalcPctMaleTotalPop: '',
        CalcFemale0to19: '',
        CalcFemale20to44: '',
        CalcFemale45to64: '',
        CalcFemale65Plus: '',
        CalcMale0to19: '',
        CalcMale20to44: '',
        CalcMale45to64: '',
        CalcMale65Plus: '',
        // Race variables:
        CalcWhitePct: '',
        CalcBlackPct: '',
        CalcAsianPct: '',
        CalcHLPct: '',
        CalcOtherRacePct: '',
        CalcTwoOrMorePct: '',
        // Economic variables:
        EstHouseholdMedianIncomeFormat: '',
        EstPopBelowFedPovertyLvlFormat: '',
        CalcPopBelowFedPovertyLvlPct: '',
        EstEdAttain25PlusHSHigherFormat: '',
        CalcPctEdAttain25PlusHSorHigher: '',
        EstUnempRateTotalPop16Plus: '',
    });

    // Hooks:
    const handleError = useErrorHandler();

    // Effects:

    useEffect(() => {
        // These 'popups' contain the same data but are seperated as two different variables to establish if we are viewing the demographic data within a chart or a map.
        // Having two seperate types avoids stale popup content evaluating to truthy when jumping from a chart to a map which causes a double fire of the effect.
        const popupVersion = chartDemographicContent || mapDemographicContent;

        // Resets demo profile to show statewide info when changing over from selectable chart with county selected, to non-selectable, statewide data only chart.
        if (
            currentTab?.contentType === 'chart' &&
            !currentTab.chartSettings.selectable
        ) {
            popupVersion.properties.MDCode = '000';
        }

        // Determine if viewing tract:
        const tract = popupVersion.properties.TractCode;

        // 000 is statewide, 888 is nationwide, all others are county.
        const endpointObj = {
            '000': config.statewideACSEndpoint,
            888: config.nationwideACSEndpoint,
        };

        const urlConfigure =
            endpointObj[popupVersion.properties.MDCode] ||
            config.countyACSEndpoint;

        // Currently, only cancers utilize census tract data from 2019. TODO: Reconfigure upon receipt of new cancer data.
        const tractConfigure = currentTopic.topicUrlPath.startsWith('cancer')
            ? config.tractACSEndpoint2019
            : config.tractACSEndpoint2023;

        // Use appropriate endpoint and parameter value for tract / county view
        const endpoint = tract
            ? `${tractConfigure}%27${tract}%27`
            : `${urlConfigure}%27${popupVersion.properties.MDCode}%27`;

        fetch(endpoint)
            .then(handleFetchError)
            .then((res) => res.json())
            .then((json) => {
                // Extracting the following nested variables with a fallback at each level to ensure a TypeError is not thrown.
                // Destructure will expect either an array or an object -- if destructuring results in an `undefined` at any point, will fallback on the expected type.
                const { features } = json || {};
                const [feature] = features || [];
                let { attributes } = feature || {};

                // TODO: This conditional is in place as the nationwide data present for only the Birth Outcomes
                // data has no returned attributes and breaks the page:
                // Certain fields need to include commas when displayed in the demographic profile:
                if (attributes) {
                    attributes = formatAttributesWithCommas(attributes);
                }

                const {
                    // Jurisdiction codes:
                    MDCode,
                    GEOID,
                    Geotype,
                    // Age variables:
                    CalcFemale0to19,
                    CalcFemale20to44,
                    CalcFemale45to64,
                    CalcFemale65Plus,
                    CalcMale0to19,
                    CalcMale20to44,
                    CalcMale45to64,
                    CalcMale65Plus,
                    // Race variables:
                    CalcWhitePct,
                    CalcBlackPct,
                    CalcAsianPct,
                    CalcHLPct,
                    CalcOtherRacePct,
                    CalcTwoOrMorePct,
                } = attributes || {};

                setState((prevState) => ({ ...prevState, ...attributes }));

                // Note: the following categories MUST remain in this order to match with the order of the "labels" array defined at the top of this file
                const racialBreakdown = [
                    CalcWhitePct,
                    CalcBlackPct,
                    CalcAsianPct,
                    CalcHLPct,
                    CalcOtherRacePct,
                    CalcTwoOrMorePct,
                ];

                const customLabels = labels.map(
                    (label, i) =>
                        `${label} - ${parseFloat(racialBreakdown[i]).toFixed(
                            1
                        )}%`
                );

                const raceChartData = {
                    maintainAspectRatio: false,
                    responsive: true,
                    labels: customLabels,
                    datasets: [
                        {
                            label: `${popupVersion.properties.County} Demographics`,

                            data: racialBreakdown.map((datum) =>
                                parseFloat(datum).toFixed(1)
                            ),
                            backgroundColor: muiTheme.piePalette.default,
                            hoverBackgroundColor: muiTheme.piePalette.hover,
                        },
                    ],
                };

                setRaceData(raceChartData);

                const ageChartData = {
                    labels: ['Male', 'Female'],
                    datasets: [
                        {
                            label: '0-19',
                            data: [
                                parseFloat(CalcMale0to19).toFixed(),
                                parseFloat(CalcFemale0to19).toFixed(),
                            ],
                        },
                        {
                            label: '20-44',
                            data: [
                                parseFloat(CalcMale20to44).toFixed(),
                                parseFloat(CalcFemale20to44).toFixed(),
                            ],
                        },
                        {
                            label: '45-64',
                            data: [
                                parseFloat(CalcMale45to64).toFixed(),
                                parseFloat(CalcFemale45to64).toFixed(),
                            ],
                        },
                        {
                            label: '65+',
                            data: [
                                parseFloat(CalcMale65Plus).toFixed(),
                                parseFloat(CalcFemale65Plus).toFixed(),
                            ],
                        },
                    ],
                };

                ageChartData.datasets.forEach((dataset, index) => {
                    dataset.backgroundColor =
                        muiTheme.stackedBarsPalette.default[index];
                    dataset.hoverBackgroundColor =
                        muiTheme.stackedBarsPalette.hover[index];
                });
                setAgeData(ageChartData);

                // Create ACS external link
                let currentLink = `https://www.census.gov/acs/www/data/data-tables-and-tools/narrative-profiles/${
                    /* Set year for ACS external link. Cancer counties should use 2023; Cancer census tracts should use 2019;
                    Everything else should use 2023. TODO: remove this logic once cancer data is updated */
                    // eslint-disable-next-line no-nested-ternary
                    Geotype === 'county'
                        ? '2023'
                        : currentTopic.topicUrlPath.startsWith('cancer')
                        ? '2019'
                        : '2023'
                }/report.php?geotype=${Geotype}&state=24&county=${MDCode}${
                    Geotype === 'tract' ? `&tract=${GEOID?.slice(-6)}` : ''
                }`;

                const acsOverwriteLinks = {
                    '000':
                        'https://www.census.gov/acs/www/data/data-tables-and-tools/narrative-profiles/2023/report.php?geotype=state&state=24', // Statewide
                    '888':
                        'https://www.census.gov/acs/www/data/data-tables-and-tools/narrative-profiles/2023/report.php?geotype=nation&usVal=us', // Nationwide
                };

                // Nationwide and statewide data have fixed unique parameters.
                // Overwriting the constructed 'currentLink' if viewing statewide or nationwide data,
                // indicated by MDCode 000 and 888, respectively.
                currentLink =
                    acsOverwriteLinks[popupVersion.properties.MDCode] ??
                    currentLink;

                setAcsLink(currentLink);
            })
            .catch(handleError);
    }, [
        handleError,
        mapDemographicContent,
        chartDemographicContent,
        currentTopic.topicUrlPath,
        currentTab,
    ]);

    return (
        <div
            id="community-profile"
            className={`community-profile-container ${
                Object.keys(raceData).length ? '' : 'not-yet-loaded'
            }`}
        >
            <div className="profile-title">
                {`${state.FullCounty} Demographic Profile`}
                {state.Geotype === 'tract' ? ` - ${state.TractName}` : ''}
            </div>
            <div
                style={{
                    borderBottom: '1px solid rgba(100,100,100,0.2)',
                    paddingBottom: '10px',
                }}
            >
                <small
                    style={{
                        display: 'block',
                        textAlign: 'center',
                        margin: '0 auto',
                    }}
                >
                    {`Source: Census Bureau American Community Survey (ACS)
                    ${
                        /* Only cancers use the 2015-2019 data for census tracts; counties use 2019-2023. 
                           Trend line charts make use of 'state' as the Geotype for Statewide. All other topics  
                           only use 2019-2023 TODO: reconfigure to remove this logic once we get new cancer data 
                        */
                        !currentTopic.topicUrlPath.startsWith('cancer') ||
                        state.Geotype === 'county' ||
                        state.Geotype === 'state'
                            ? '2019-2023'
                            : '2015-2019'
                    }`}
                </small>
                <div className="chart-info-modal-trigger-container">
                    <Button
                        size="small"
                        onClick={() => setChartInfoModalOpen(true)}
                    >
                        <InfoOutlinedIcon />
                    </Button>
                </div>
            </div>
            <div className="community-figures">
                <div className="row-section">
                    <div className="row-cell">
                        <div className="row-title">Total Population</div>
                        <div className="row-data">
                            {state.EstTotalPopFormat}
                        </div>
                    </div>
                    <div className="row-cell">
                        <div className="row-title">Female</div>
                        <div className="row-data">
                            {state.EstFemaleTotalPopFormat} (
                            {state.CalcPctFemaleTotalPop})
                        </div>
                    </div>
                    <div className="row-cell">
                        <div className="row-title">Male</div>
                        <div className="row-data">
                            {state.EstMaleTotalPopFormat} (
                            {state.CalcPctMaleTotalPop})
                        </div>
                    </div>
                </div>
            </div>
            <div className="profile-content">
                <div className="chart-section">
                    <div className="community-charts">
                        <div className="age-sex-container">
                            {Object.keys(ageData).length ? (
                                <Bar
                                    ref={barRef}
                                    data={ageData}
                                    options={{
                                        indexAxis: 'y',
                                        plugins: {
                                            legend: {
                                                labels: {
                                                    boxWidth: 12,
                                                },
                                            },
                                            title: {
                                                text: 'Age-Sex Breakdown',
                                                display: true,
                                            },
                                            tooltip: {
                                                mode: 'index',
                                                intersect: true,
                                                yAlign: 'bottom',
                                                callbacks: {
                                                    label: ({
                                                        dataIndex,
                                                        datasetIndex,
                                                        dataset,
                                                    }) => {
                                                        // Percent values (not available from chart.js event)
                                                        // (Ordered at top level male then female to match chart.js index order, then ordered least to greatest by age to match chart.js dataset index order)
                                                        const ageSexValues = [
                                                            [
                                                                state.CalcPctMale0to19,
                                                                state.CalcPctMale20to44,
                                                                state.CalcPctMale45to64,
                                                                state.CalcPctMale65Plus,
                                                            ],
                                                            [
                                                                state.CalcPctFemale0to19,
                                                                state.CalcPctFemale20to44,
                                                                state.CalcPctFemale45to64,
                                                                state.CalcPctFemale65Plus,
                                                            ],
                                                        ];
                                                        // Find current count value, then append corresponding percent value
                                                        const value =
                                                            dataset.data[
                                                                dataIndex
                                                            ];
                                                        const {
                                                            label,
                                                        } = dataset;
                                                        const percentValue =
                                                            ageSexValues[
                                                                dataIndex
                                                            ][datasetIndex];
                                                        return `${label} yrs: ${Number(
                                                            value
                                                        ).toLocaleString()} (${percentValue})`;
                                                    },
                                                },
                                            },
                                        },
                                        layout: {
                                            padding: {
                                                top: 17,
                                            },
                                        },

                                        maintainAspectRatio: false,

                                        scales: {
                                            x: {
                                                stacked: true,
                                                title: {
                                                    display: true,
                                                    text: 'Population',
                                                    padding: 0,
                                                    font: {
                                                        weight: 'bold',
                                                    },
                                                },
                                                ticks: {
                                                    // Custom step size for dif population sizes numbers to reduce vertical chart size compression.
                                                    stepSize: (chartInfo) =>
                                                        determineChartSteps(
                                                            chartInfo.scale.max
                                                        ),
                                                    // Converts x-axis population label to thousands.
                                                    callback: (value) =>
                                                        Number(
                                                            value
                                                        ).toLocaleString(),
                                                },
                                            },
                                            y: {
                                                stacked: true,
                                                ticks: {
                                                    maxTicksLimit: 5,
                                                },
                                            },
                                        },
                                    }}
                                />
                            ) : null}
                        </div>
                        <hr className="chart-divider" />
                        <div className="pie-container">
                            {Object.keys(raceData).length ? (
                                <Pie
                                    ref={pieRef}
                                    data={raceData}
                                    options={{
                                        plugins: {
                                            title: {
                                                text:
                                                    'Racial/Ethnic Composition',
                                                display: true,
                                                padding: 0,
                                            },
                                            legend: {
                                                position: 'right',
                                                align: 'end',
                                            },
                                            tooltip: {
                                                callbacks: {
                                                    label: ({
                                                        dataIndex,
                                                        dataset,
                                                    }) => {
                                                        const value =
                                                            dataset.data[
                                                                dataIndex
                                                            ];
                                                        return `${value}%`;
                                                    },
                                                    title: ([selected]) =>
                                                        labels[
                                                            selected.dataIndex
                                                        ],
                                                },
                                            },
                                        },
                                        maintainAspectRatio: false,
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                    <div className="community-bottom-text">
                        <p>{`Click on chart legends to filter by category`}</p>
                    </div>
                </div>
                <div className="community-figures">
                    <div className="cell-section">
                        <div className="figure-cell">
                            <div className="cell-title">Median Income</div>
                            <div className="cell-info">
                                {state.EstHouseholdMedianIncomeFormat}
                            </div>
                        </div>
                        <div className="figure-cell">
                            <div className="cell-title">People in Poverty</div>
                            <div className="cell-info">
                                {state.EstPopBelowFedPovertyLvlFormat} (
                                {state.CalcPopBelowFedPovertyLvlPct})
                            </div>
                        </div>
                        <div className="figure-cell">
                            <div className="cell-title">
                                Finished High School (or above)
                            </div>
                            <div className="cell-info">
                                {state.EstEdAttain25PlusHSHigherFormat} (
                                {state.CalcPctEdAttain25PlusHSorHigher})
                            </div>
                        </div>
                        <div className="figure-cell">
                            <div className="cell-title">Unemployment Rate</div>
                            <div className="cell-info">
                                {state.EstUnempRateTotalPop16Plus}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="community-data-source-link">
                <small>
                    Discover{' '}
                    <a target="_blank" rel="noopener noreferrer" href={acsLink}>
                        more
                    </a>{' '}
                    about this location.
                </small>
            </div>
        </div>
    );
};

export default CommunityProfile;
